import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AppTableSource, ColumnSortType } from '../../model/table.model';
import { CrmService } from '../../services/crm.service';
import { tableManagersHeaders } from '../../modules/crm/team/components/team/team.interface';
import { IError } from '../../interfaces';
import { ERROR_MESSAGES, activeTranslations, PERMISSIONS, USER_COMPANY_STATUS } from '../../enum/constants';
import { NotificationService } from '../../services/notifications.service';
import { IParams } from '../../model/http';
import { initItemsParams } from '../../services/channel.service';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { SidebarType } from '../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { EmployeeProfilePayload } from '../../components/layout/right-sidebar/components/employee-profile/employee-profile.component';
import { DialogService } from '../../services/dialog.service';
import { environment } from '../../../environments/environment';
import { SelectionModel } from '@angular/cdk/collections';
import { BaseService } from '../../services/base.service';
import { WebSocketService } from '../../services/gateway.service';

@Component({
  selector: 'app-tab-managers',
  templateUrl: './tab-managers.component.html',
  styleUrl: './tab-managers.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabManagersComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$ = new Subject<null>();

  // Var`s
  public isLoader: boolean = false;
  public visibleColumns: string[] = [];
  public itemsRequest: any;
  public timerSearch: any;
  public canViewTeam: boolean = false;
  public canManageTeam: boolean = false;
  public canChangeOwnRole: boolean = false;
  public suspendManagerOptions: any[] = [];
  public suspendNewManagerId: string | null = null;

  @ViewChild('suspendReassignTemplate', { static: true }) private suspendReassignTemplate!: TemplateRef<any>;

  // Table relative
  private dataSort: any;
  public dataSource$: Observable<AppTableSource> = this.crmService.itemsManagers$.pipe(
    map((v) => {
      tableManagersHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });

      return {
        headers: this.getTableHeaders(),
        rows: (v.items || []).map((item: any) => {
          // app-avatar resolves the key itself and shows the `full_name` initials without a photo
          const url = item.avatar_url || null;
          const isOwner = item.role === 'OWNER';
          const isCurrentUser = item.id === this.baseService.currentUserInfo?.id;

          const role = isOwner
            ? activeTranslations.owner
            : activeTranslations.manager;
          return {
            ...item,
            image_url: url,
            role,
            is_owner: isOwner,
            can_update_manager: !isOwner && (this.canManageTeam || (this.canChangeOwnRole && isCurrentUser)),
            can_manage_team_action: this.canManageTeam && !isOwner,
          };
        }),
        pageSize: v?.size || 0,
        pageIndex: v.page ? v.page - 1 : 0,
        length: v.total || 0,
      };
    }),
  );

  public selectionTable = new SelectionModel<any>(true, []);
  public bulkActionList: any[] = [
    { label: 'change_role', value: 'CHANGE_ROLE' },
    { label: 'delete', value: 'DELETE_MANAGERS' },
  ];

  constructor(
    private readonly crmService: CrmService,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly baseService: BaseService,
    private readonly webSocketService: WebSocketService,
  ) {}

  public ngOnInit(): void {
    this.canManageTeam = this.baseService.isHasPermission(PERMISSIONS.TEAM_ACTION);
    this.canViewTeam = this.baseService.isHasPermission(PERMISSIONS.TEAM_ACCESS) || this.canManageTeam;
    this.canChangeOwnRole = this.baseService.isHasPermission(PERMISSIONS.ROLE_CHANGE_OWN_ROLE_ACCESS);
    this.visibleColumns = this.getTableHeaders().map((v) => v.value);
    this.onLoadListManagers();

    this.webSocketService
      .onManagerStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ manager_id, status, last_active_at }) =>
        this.crmService.patchManagerPresence(manager_id, status, last_active_at),
      );
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public onLoadListManagers(): void {
    this.isLoader = true;
    this.selectionTable.clear();
    if (!this.canViewTeam && this.canChangeOwnRole) {
      this.loadCurrentManager();
      return;
    }

    this.crmService
      .loadListManagers()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.isLoader = false;
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public getItemsWithUpdatedParams(params: IParams): void {
    if (!this.canViewTeam && this.canChangeOwnRole) {
      this.loadCurrentManager();
      return;
    }

    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }

    this.isLoader = true;
    this.selectionTable.clear();
    this.itemsRequest = this.crmService
      .setQueryManagersParams(params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res: any) => {
          this.isLoader = false;
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams({
              page: res.page - 1,
              take: res.take,
            });
          }
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onSearchChange(search: string): void {
    if (this.timerSearch) clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams({
        ...initItemsParams,
        search: search,
      });
    }, 500);
  }

  public onSortChange(e: any): void {
    this.dataSort = e;
    this.getItemsWithUpdatedParams({
      sort: e.active,
      order: e.direction,
    });
  }

  public onPaginationChange(e: any): void {
    const { pageIndex, pageSize } = e;
    const page = pageIndex + 1;
    const take = pageSize;
    this.getItemsWithUpdatedParams({ page, take });
  }

  public onActionChange(e: any): void {
    if (!this.canManageTeam && !(this.canChangeOwnRole && e.id === this.baseService.currentUserInfo?.id)) return;

    if (e.label === 'EDIT') {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.UpdateManager,
            maxWidth: '50vw',
          },
          { data: { manager_id: e.id } },
        )
        .pipe(take(2))
        .subscribe((res: any) => {
          if (res === 'success') {
            this.onLoadListManagers();
          }
        });
    }

    if (e.label === 'LOGOUT') {
      this.crmService
        .logoutManager(e.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.manager_logged_out,
            );
            this.onLoadListManagers();
          },
          error: (err: any) => {
            (err.error?.errors || []).forEach((error: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[error.code]);
            });
          },
        });
    }

    if (e.label === 'RESEND_INVITE') {
      this.runEmployeeAction(
        this.crmService.resendEmployeeInvite(e.membership_id),
        activeTranslations.notifications_texts.success.successfully_resent,
      );
    }

    if (e.label === 'CANCEL_INVITE') {
      this.dialogService
        .openConfirm({ text: 'dialogs.tab_managers_cancel_invite' })
        .pipe(takeUntil(this.destroy$))
        .subscribe((confirmed: any) => {
          if (!confirmed) return;

          this.runEmployeeAction(
            this.crmService.cancelEmployeeInvite(e.membership_id),
            activeTranslations.notifications_texts.success.successfully_canceled,
          );
        });
    }

    if (e.label === 'SUSPEND') {
      this.suspendNewManagerId = null;
      this.suspendManagerOptions = (this.crmService.getManagersSnapshot()?.items || [])
        .filter((item: any) => item.id !== e.id && item.membership_status === USER_COMPANY_STATUS.ACTIVE)
        .map((item: any) => ({ id: item.id, name: item.full_name, avatarUrl: item.avatar_url || undefined }));

      this.dialogService
        .openConfirm({ text: 'dialogs.tab_managers_suspend', template: this.suspendReassignTemplate })
        .pipe(takeUntil(this.destroy$))
        .subscribe((confirmed: any) => {
          if (!confirmed) return;

          this.runEmployeeAction(
            this.crmService.suspendEmployee(e.id, this.suspendNewManagerId),
            activeTranslations.notifications_texts.success.employee_suspended,
          );
        });
    }

    if (e.label === 'RESTORE') {
      this.runEmployeeAction(
        this.crmService.restoreEmployee(e.id),
        activeTranslations.notifications_texts.success.employee_restored,
      );
    }

    if (e.label === 'DELETE') {
      if (e.is_owner) {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.cannot_remove_owner,
        );
        return;
      }
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.tab_managers_1',
      });

      dialogRef.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          if (res) {
            this.crmService
              .deleteManager(e.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.notificationService.showSuccess(
                    activeTranslations.notifications_texts.success
                      .successfully_deleted,
                    '',
                  );
                  this.onLoadListManagers();
                },
                error: (e: any) => {
                  (e.error?.errors || []).forEach((err: IError) => {
                    this.notificationService.showError('', ERROR_MESSAGES[err.code]);
                  });
                },
              });
          }
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
    }
  }

  public onRowClick(e: any): void {
    const payload: EmployeeProfilePayload = { employeeId: e.id, canEdit: !!e.can_update_manager };

    this.stackedSidebarService
      .openSidebar({ type: SidebarType.EmployeeProfile, width: '960px' }, payload)
      .pipe(take(1))
      .subscribe(() => this.onLoadListManagers());
  }

  public onInviteEmployees(): void {
    if (!this.canManageTeam) return;

    this.stackedSidebarService
      .openSidebar({ type: SidebarType.InviteEmployees, width: '100vw' }, {})
      .pipe(take(1))
      .subscribe((res) => {
        if (res !== 'success') return;

        this.notificationService.showSuccess('', activeTranslations.notifications_texts.success.successfully_sent);
        this.onLoadListManagers();
      });
  }

  private runEmployeeAction(request: Observable<unknown>, successMessage: string): void {
    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.notificationService.showSuccess('', successMessage);
        this.onLoadListManagers();
      },
      error: (err: any) => {
        (err.error?.errors || []).forEach((error: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[error.code]);
        });
      },
    });
  }

  public bulkActionPopup(event: any): void {
    if (!this.canManageTeam) return;

    const ids = this.selectionTable.selected.map((v) => v?.id);
    const ownersSelected = this.selectionTable.selected.filter((v) => v?.is_owner);

    if (event.value === 'CHANGE_ROLE') {
      if (ownersSelected.length) {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.cannot_remove_owner,
        );
        return;
      }
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.UpdateManager,
            maxWidth: '50vw',
          },
          { data: { managers: ids } },
        )
        .pipe(take(2))
        .subscribe((res: any) => {
          if (res === 'success') {
            this.onLoadListManagers();
          }
        });
    }

    if (event.value === 'DELETE_MANAGERS') {
      if (ownersSelected.length) {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.cannot_remove_owner,
        );
        return;
      }
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.tab_managers_2',
      });

      dialogRef.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          if (res) {
            this.crmService
              .bulkDeleteManager(ids)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.notificationService.showSuccess(
                    activeTranslations.notifications_texts.success
                      .successfully_deleted,
                    '',
                  );
                  this.onLoadListManagers();
                },
                error: (e: any) => {
                  (e.error?.errors || []).forEach((err: IError) => {
                    this.notificationService.showError('', ERROR_MESSAGES[err.code]);
                  });
                },
              });
          }
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
    }
  }

  private getTableHeaders(): any[] {
    return tableManagersHeaders.filter((column) => {
      if (column.value === 'primary') return this.canManageTeam;
      if (column.value === 'action') return this.canManageTeam || this.canChangeOwnRole;
      return true;
    });
  }

  private loadCurrentManager(): void {
    const userId = this.baseService.currentUserInfo?.id;
    if (!userId) {
      this.crmService.setManagersItems({ items: [], total: 0, size: 1, page: 1 });
      this.isLoader = false;
      return;
    }

    this.crmService
      .getManagerById(userId)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (manager: any) => {
          this.crmService.setManagersItems({
            items: manager ? [manager] : [],
            total: manager ? 1 : 0,
            size: 1,
            page: 1,
          });
          this.isLoader = false;
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }
}
